import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Plus, Trash2, X, Loader2, Search, Pencil, Eye, EyeOff, ExternalLink, Star, Upload, ImageIcon,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { AdminModal } from "@/components/AdminModal";
import { Header } from "./Schools";
import { formatPublishDate } from "@/lib/podcast";

const resolve = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

const EMPTY_FORM = {
  title: "", slug: "", excerpt: "", content: "", author: "", category: "",
  tags: [], featured_image: "", gallery: [], embedded_videos: [], audio_url: "",
  publish_date: "", status: "draft", featured: false,
  seo_title: "", seo_description: "", og_image: "",
};

const slugify = (s) =>
  (s || "").toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

export default function AdminPodcast() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [editing, setEditing] = useState(null); // null | "new" | post object
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (q) params.q = q;
    api.get("/podcast/admin/posts", { params })
      .then((r) => setPosts(r.data || []))
      .catch(() => { toast.error("Could not load posts"); setPosts([]); })
      .finally(() => setLoading(false));
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY_FORM); setSlugTouched(false); setEditing("new"); };
  const openEdit = (p) => {
    setForm({ ...EMPTY_FORM, ...p, tags: p.tags || [], gallery: p.gallery || [], embedded_videos: p.embedded_videos || [] });
    setSlugTouched(true);
    setEditing(p);
  };
  const closeEditor = () => { setEditing(null); setForm(EMPTY_FORM); };

  const onTitleChange = (title) => {
    setForm((f) => ({ ...f, title, slug: slugTouched ? f.slug : slugify(title) }));
  };

  const save = async () => {
    if (!form.title || !form.slug) { toast.error("Title and slug are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, publish_date: form.publish_date || null };
      if (editing === "new") {
        await api.post("/podcast/posts", payload);
        toast.success("Post created");
      } else {
        await api.put(`/podcast/posts/${editing.id}`, payload);
        toast.success("Post updated");
      }
      closeEditor();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save post");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    setBusyIds((s) => new Set(s).add(id));
    try {
      await api.delete(`/podcast/posts/${id}`);
      toast.success("Post deleted");
      load();
    } catch { toast.error("Failed to delete"); }
    finally { setBusyIds((s) => { const n = new Set(s); n.delete(id); return n; }); }
  };

  const toggleStatus = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    setBusyIds((s) => new Set(s).add(p.id));
    try {
      const fd = new FormData();
      fd.append("status", next);
      await api.patch(`/podcast/posts/${p.id}/status`, fd);
      toast.success(next === "published" ? "Published" : "Unpublished");
      load();
    } catch { toast.error("Failed to update status"); }
    finally { setBusyIds((s) => { const n = new Set(s); n.delete(p.id); return n; }); }
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/podcast/upload", fd);
    return r.data.url;
  };

  const onCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, featured_image: url }));
    } catch (err) { toast.error(err?.response?.data?.detail || "Cover upload failed"); }
    finally { setUploadingCover(false); }
  };

  const onGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const urls = await Promise.all(files.map((f) => uploadFile(f)));
      setForm((f) => ({ ...f, gallery: [...f.gallery, ...urls] }));
    } catch (err) { toast.error(err?.response?.data?.detail || "Gallery upload failed"); }
  };

  const onAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAudio(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, audio_url: url }));
    } catch (err) { toast.error(err?.response?.data?.detail || "Audio upload failed"); }
    finally { setUploadingAudio(false); }
  };

  const addVideo = () => setForm((f) => ({ ...f, embedded_videos: [...f.embedded_videos, ""] }));
  const setVideo = (i, v) => setForm((f) => ({ ...f, embedded_videos: f.embedded_videos.map((x, idx) => (idx === i ? v : x)) }));
  const removeVideo = (i) => setForm((f) => ({ ...f, embedded_videos: f.embedded_videos.filter((_, idx) => idx !== i) }));

  const setTagsFromInput = (val) =>
    setForm((f) => ({ ...f, tags: val.split(",").map((t) => t.trim()).filter(Boolean) }));

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <Header
        title="Podcast"
        subtitle={`${posts.length} post${posts.length === 1 ? "" : "s"}`}
        actions={
          <button
            onClick={openNew}
            data-testid="podcast-new-post"
            className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-3.5 py-2 rounded-lg hover:bg-[#0F172A]/90 transition-colors"
          >
            <Plus className="w-4 h-4" /> New post
          </button>
        }
      />

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search posts…"
            data-testid="podcast-admin-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 outline-none text-sm bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
          />
        </div>
        <div className="flex gap-2">
          {["all", "published", "draft"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${
                statusFilter === s ? "bg-[#0F172A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="mt-6 space-y-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : posts.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
          <div className="font-display text-lg font-medium text-[#0F172A]">No posts yet</div>
          <p className="mt-1 text-sm text-gray-500">Create your first article or episode.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {posts.map((p) => (
            <div
              key={p.id}
              data-testid={`podcast-row-${p.id}`}
              className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 hover:border-gray-300 transition-colors"
            >
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                {p.featured_image ? (
                  <img src={resolve(p.featured_image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-gray-300"><ImageIcon className="w-6 h-6" /></div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-[#0F172A] truncate">{p.title}</h3>
                  {p.featured && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400 shrink-0" />}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                  <span className={`px-1.5 py-0.5 rounded-full font-medium ${p.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                    {p.status}
                  </span>
                  {p.category && <><span className="text-gray-300">·</span><span>{p.category}</span></>}
                  {p.publish_date && <><span className="text-gray-300">·</span><span>{formatPublishDate(p.publish_date)}</span></>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {p.status === "published" && (
                  <a
                    href={`/podcast/${p.slug}`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-8 h-8 grid place-items-center rounded-lg hover:bg-gray-100 text-gray-500"
                    aria-label="Preview"
                    data-testid={`podcast-preview-${p.id}`}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <button
                  onClick={() => toggleStatus(p)}
                  disabled={busyIds.has(p.id)}
                  className="w-8 h-8 grid place-items-center rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-40"
                  aria-label={p.status === "published" ? "Unpublish" : "Publish"}
                  data-testid={`podcast-toggle-status-${p.id}`}
                >
                  {p.status === "published" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => openEdit(p)}
                  className="w-8 h-8 grid place-items-center rounded-lg hover:bg-gray-100 text-gray-500"
                  aria-label="Edit"
                  data-testid={`podcast-edit-${p.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => remove(p.id)}
                  disabled={busyIds.has(p.id)}
                  className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-50 text-gray-500 hover:text-rose-600 disabled:opacity-40"
                  aria-label="Delete"
                  data-testid={`podcast-delete-${p.id}`}
                >
                  {busyIds.has(p.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <AdminModal
          onClose={closeEditor}
          title={editing === "new" ? "New post" : "Edit post"}
          maxWidth="max-w-3xl"
          testId="podcast-editor"
          footer={
            <>
              <button onClick={closeEditor} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg w-full sm:w-auto">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="podcast-save"
                className="inline-flex items-center justify-center gap-1.5 bg-[#0F172A] disabled:bg-gray-400 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-[#0F172A]/90 transition-colors w-full sm:w-auto"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {saving ? "Saving…" : "Save post"}
              </button>
            </>
          }
        >
              <div className="grid gap-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    placeholder="Title*"
                    value={form.title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    data-testid="podcast-field-title"
                    className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                  />
                  <input
                    placeholder="Slug*"
                    value={form.slug}
                    onChange={(e) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: slugify(e.target.value) })); }}
                    data-testid="podcast-field-slug"
                    className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm font-mono focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                  />
                </div>

                <textarea
                  placeholder="Short excerpt (shown on cards)"
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  rows={2}
                  data-testid="podcast-field-excerpt"
                  className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 resize-none"
                />

                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Content (Markdown — headings, lists, quotes, links, images, tables, and code blocks all use standard Markdown syntax)</label>
                  <textarea
                    placeholder="Write your article here in Markdown…"
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    rows={10}
                    data-testid="podcast-field-content"
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm font-mono focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 resize-y"
                  />
                </div>

                <div className="grid sm:grid-cols-3 gap-3">
                  <input
                    placeholder="Author"
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    data-testid="podcast-field-author"
                    className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                  />
                  <input
                    placeholder="Category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="podcast-field-category"
                    className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                  />
                  <input
                    type="date"
                    value={form.publish_date ? form.publish_date.slice(0, 10) : ""}
                    onChange={(e) => setForm({ ...form, publish_date: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                    data-testid="podcast-field-publish-date"
                    className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                  />
                </div>

                <input
                  placeholder="Tags, comma separated (e.g. storytelling, production)"
                  value={form.tags.join(", ")}
                  onChange={(e) => setTagsFromInput(e.target.value)}
                  data-testid="podcast-field-tags"
                  className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                />

                {/* Cover image */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Featured image</label>
                  <div className="flex items-center gap-3">
                    {form.featured_image && (
                      <img src={resolve(form.featured_image)} alt="" className="w-16 h-16 rounded-lg object-cover border border-gray-200" />
                    )}
                    <label className="inline-flex items-center gap-1.5 border border-gray-200 hover:border-gray-300 text-sm px-3 py-2 rounded-lg cursor-pointer">
                      {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploadingCover ? "Uploading…" : "Upload"}
                      <input type="file" accept="image/*" onChange={onCoverUpload} className="hidden" data-testid="podcast-cover-upload" />
                    </label>
                  </div>
                </div>

                {/* Gallery */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Image gallery</label>
                  <div className="flex flex-wrap gap-2">
                    {form.gallery.map((g, i) => (
                      <div key={i} className="relative w-16 h-16">
                        <img src={resolve(g)} alt="" className="w-full h-full rounded-lg object-cover border border-gray-200" />
                        <button
                          onClick={() => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }))}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 grid place-items-center rounded-full bg-white border border-gray-200 text-gray-600 hover:text-rose-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <label className="w-16 h-16 grid place-items-center rounded-lg border border-dashed border-gray-300 hover:border-gray-400 cursor-pointer text-gray-400">
                      <Plus className="w-5 h-5" />
                      <input type="file" accept="image/*" multiple onChange={onGalleryUpload} className="hidden" data-testid="podcast-gallery-upload" />
                    </label>
                  </div>
                </div>

                {/* Embedded videos */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Embedded videos (YouTube or Vimeo URLs)</label>
                  <div className="space-y-2">
                    {form.embedded_videos.map((v, i) => (
                      <div key={i} className="flex gap-2">
                        <input
                          value={v}
                          onChange={(e) => setVideo(i, e.target.value)}
                          placeholder="https://youtube.com/watch?v=…"
                          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                        />
                        <button onClick={() => removeVideo(i)} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 text-gray-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button onClick={addVideo} className="inline-flex items-center gap-1 text-xs font-medium text-[#4F46E5] hover:underline">
                      <Plus className="w-3.5 h-3.5" /> Add video
                    </button>
                  </div>
                </div>

                {/* Audio */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Podcast audio (optional)</label>
                  <div className="flex items-center gap-3">
                    {form.audio_url && <audio controls src={resolve(form.audio_url)} className="h-9" />}
                    <label className="inline-flex items-center gap-1.5 border border-gray-200 hover:border-gray-300 text-sm px-3 py-2 rounded-lg cursor-pointer">
                      {uploadingAudio ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploadingAudio ? "Uploading…" : "Upload audio"}
                      <input type="file" accept="audio/*" onChange={onAudioUpload} className="hidden" data-testid="podcast-audio-upload" />
                    </label>
                  </div>
                </div>

                {/* SEO */}
                <div className="pt-3 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-600 mb-2">SEO</div>
                  <div className="grid gap-2.5">
                    <input
                      placeholder="SEO title (optional — defaults to post title)"
                      value={form.seo_title}
                      onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
                      className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10"
                    />
                    <textarea
                      placeholder="Meta description (optional — defaults to excerpt)"
                      value={form.seo_description}
                      onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
                      rows={2}
                      className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 resize-none"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                    data-testid="podcast-field-featured"
                  />
                  Feature this post
                </label>
              </div>
        </AdminModal>
      )}
    </motion.div>
  );
}
